export const COOKIE_CONSENT_NAME = "cookie_consent";
export const COOKIE_CONSENT_UPDATED_AT_NAME = "cookie_consent_updated_at";

export const CONSENT_CHOICE_MIN_TIMESTAMP_MS = Date.UTC(2020, 0, 1);
export const CONSENT_CHOICE_MAX_FUTURE_SKEW_MS = 5 * 60 * 1000;
export const LEGACY_CONSENT_CHOICE_UPDATED_AT = new Date(
  CONSENT_CHOICE_MIN_TIMESTAMP_MS,
);

/**
 * Parse a browser-supplied consent-choice timestamp without permitting a
 * far-future value to pin the durable Account preference indefinitely.
 */
export function parseConsentChoiceUpdatedAt(
  value: string | Date | null | undefined,
  nowMs = Date.now(),
): Date | null {
  const timestampMs =
    value instanceof Date ? value.getTime() : Date.parse(value ?? "");
  if (
    !Number.isFinite(timestampMs) ||
    timestampMs < CONSENT_CHOICE_MIN_TIMESTAMP_MS ||
    timestampMs > nowMs + CONSENT_CHOICE_MAX_FUTURE_SKEW_MS
  ) {
    return null;
  }
  return new Date(timestampMs);
}

/**
 * Read every same-name timestamp cookie. Duplicate path cookies are accepted
 * only when they resolve to the same valid instant; ambiguity fails closed.
 */
export function parseConsentChoiceUpdatedAtCookie(
  cookieHeader: string | null | undefined,
  nowMs = Date.now(),
): Date | null {
  if (!cookieHeader) return null;

  const values = cookieHeader.split(";").flatMap((part) => {
    const separator = part.indexOf("=");
    if (
      separator < 0 ||
      part.slice(0, separator).trim() !== COOKIE_CONSENT_UPDATED_AT_NAME
    ) {
      return [];
    }
    try {
      return [decodeURIComponent(part.slice(separator + 1).trim())];
    } catch {
      return [null];
    }
  });

  if (values.length === 0 || values.some((value) => value === null)) {
    return null;
  }
  const parsed = values.map((value) =>
    parseConsentChoiceUpdatedAt(value, nowMs),
  );
  const firstMs = parsed[0]?.getTime();
  if (
    firstMs === undefined ||
    parsed.some((value) => value === null || value.getTime() !== firstMs)
  ) {
    return null;
  }
  return new Date(firstMs);
}
